import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { PageShell } from "../../shared/layouts/PageShell";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { useAuth } from "../../../providers/AuthProvider";
import { ApiError } from "../../../services/apiClient";
import {
  declineWorkspaceInvitation,
  getWorkspaceInvitationPreview,
  type WorkspaceInvitationAcceptanceResponse,
  type WorkspaceInvitationPreview,
} from "../api/workspaces";
import { WorkspaceInvitationAcceptButton } from "./WorkspaceMembersPage";
import { storage } from "../../shared/api/storage";
import { AUTH_REDIRECT_KEY } from "../../../providers/AuthProvider";

import {
  ArrowLeft,
  ArrowRight,
  SignIn,
  SquaresFourIcon,
  UserPlus,
  XCircle,
} from "@phosphor-icons/react";
import { defaultIconProps } from "../../shared/ui/icons";
import { ConfirmDialog } from "../../../components/ConfirmDialog";

const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const cardClasses = "rounded-2xl border border-slate-200 bg-white p-8 shadow-sm flex flex-col gap-6 text-center dark:border-slate-800 dark:bg-slate-900";
const successCardClasses = "rounded-2xl border border-green-200 bg-green-50 p-8 text-center text-green-800 dark:border-green-900/50 dark:bg-green-900/20 dark:text-green-100";
const actionStackClasses = "flex flex-col gap-3 w-full sm:flex-row sm:justify-center";
const ghostButtonClasses = "pm-btn pm-btn-ghost";
const primaryButtonClasses = "pm-btn pm-btn-primary";
const secondaryButtonClasses = "pm-btn pm-btn-surface";
const dangerButtonClasses = "pm-btn pm-btn-danger";

export function WorkspaceInvitationLandingPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [accepted, setAccepted] = useState<WorkspaceInvitationAcceptanceResponse | null>(null);
  const [preview, setPreview] = useState<WorkspaceInvitationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [declineSuccess, setDeclineSuccess] = useState(false);
  const [declineError, setDeclineError] = useState<string | null>(null);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t("workspaces.invitations.token_missing"));
    }
  }, [token, t]);

  useEffect(() => {
    if (!token) {
      setPreview(null);
      setIsPreviewLoading(false);
      return;
    }
    setIsPreviewLoading(true);
    setPreview(null);
    getWorkspaceInvitationPreview(token)
      .then((data) => {
        setPreview(data);
        setError(null);
      })
      .catch((err) => {
        const message =
          err instanceof ApiError
            ? err.message
            : t("workspaces.invitations.preview_error");
        setError(message);
      })
      .finally(() => {
        setIsPreviewLoading(false);
      });
  }, [token, t]);

  const expiresMessage = useMemo(() => {
    if (!preview) {
      return null;
    }
    if (typeof preview.expires_in_seconds === "number") {
      return t("workspaces.invitations.expires_in", {
        duration: formatDuration(preview.expires_in_seconds),
      });
    }
    if (typeof preview.expired_for_seconds === "number") {
      return t("workspaces.invitations.expired_for", {
        duration: formatDuration(preview.expired_for_seconds),
      });
    }
    return null;
  }, [preview, t]);

  const invitationStatus = preview?.invitation.status ?? "pending";
  const isExpired = typeof preview?.expired_for_seconds === "number";
  const canRespond =
    Boolean(accessToken) &&
    Boolean(preview) &&
    invitationStatus === "pending" &&
    !isExpired &&
    !accepted &&
    !declineSuccess;

  const statusBanner = useMemo(() => {
    if (!preview) {
      return null;
    }
    if (invitationStatus === "pending" && !isExpired) {
      return null;
    }
    if (invitationStatus === "revoked") {
      return t("workspaces.invitations.status_revoked");
    }
    if (invitationStatus === "accepted") {
      return t("workspaces.invitations.status_accepted");
    }
    if (isExpired) {
      return t("workspaces.invitations.status_expired");
    }
    return null;
  }, [invitationStatus, isExpired, preview, t]);

  const workspaceLink = useMemo(() => {
    if (!accepted) {
      return "";
    }
    return `/workspaces/${accepted.workspace.id}/lots`;
  }, [accepted]);

  const invitationPath = useMemo(
    () => window.location.pathname + window.location.search,
    []
  );

  const rememberRedirect = () => {
    storage.set(AUTH_REDIRECT_KEY, invitationPath);
  };

  const handleDeclineInvitation = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    setIsDeclining(true);
    setDeclineError(null);
    try {
      const updated = await declineWorkspaceInvitation(accessToken, token);
      setPreview((prev) =>
        prev
          ? {
              ...prev,
              invitation: updated,
            }
          : prev,
      );
      setDeclineSuccess(true);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("workspaces.invitations.reject_error");
      setDeclineError(message);
    } finally {
      setIsDeclining(false);
    }
  }, [accessToken, t, token]);

  const handleBack = useCallback(() => {
    const historyState = window.history.state as { idx?: number } | null;
    const canNavigateBack =
      typeof historyState?.idx === "number" && historyState.idx > 0;

    if (canNavigateBack) {
      navigate(-1);
      return;
    }

    navigate("/dashboard", { replace: true });
  }, [navigate]);

  return (
    <PageShell className={pageStackClasses} width="wide">
      <PageHeader
        title={t("workspaces.invitations.title")}
        subtitle={t("workspaces.invitations.subtitle")}
        actions={
          <div className="flex items-center gap-3">
            <button
              type="button"
              className={ghostButtonClasses}
              onClick={handleBack}
            >
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {t("actions.back")}
            </button>
          </div>
        }
      />

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 text-center dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300" role="alert">
          {error}
        </div>
      )}

      {!token && (
        <p className="text-center text-slate-500 dark:text-slate-400">
          {t("workspaces.invitations.check_link")}
        </p>
      )}

      {token && !accepted && isPreviewLoading && !error && (
        <p className="text-center text-slate-500 dark:text-slate-400">
          {t("workspaces.invitations.loading_details")}
        </p>
      )}

      {token && preview && !accepted && !error && (
        <section className={cardClasses} role="status">
          <div className="text-lg text-slate-700 dark:text-slate-300">
            <span
              dangerouslySetInnerHTML={{
                __html: t("workspaces.invitations.summary_html", {
                  inviter: `<strong class="font-semibold text-slate-900 dark:text-slate-100">${preview.inviter_name || t("workspaces.invitations.inviter_fallback")}</strong>`,
                  workspace: `<strong class="font-semibold text-slate-900 dark:text-slate-100">${preview.workspace_name}</strong>`,
                })
              }}
            />
          </div>
          {preview.workspace_description ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {preview.workspace_description}
            </p>
          ) : null}
          {(expiresMessage || statusBanner) && (
            <div className="flex flex-col gap-1 text-sm font-medium text-slate-600 dark:text-slate-400">
              {expiresMessage ? <p>{expiresMessage}</p> : null}
              {statusBanner ? <p className="text-rose-600 dark:text-rose-400">{statusBanner}</p> : null}
            </div>
          )}
          {declineError && (
            <div className="text-sm text-rose-600 dark:text-rose-400" role="alert">
              {declineError}
            </div>
          )}
        </section>
      )}

      {token && !accepted && !declineSuccess && (
        <section className={cardClasses}>
          <p className="text-slate-700 dark:text-slate-300">
            {accessToken
              ? canRespond
                ? t("workspaces.invitations.accept_prompt")
                : t("workspaces.invitations.cannot_accept")
              : t("workspaces.invitations.signin_prompt")}
          </p>

          {accessToken ? (
            canRespond ? (
              <div className={actionStackClasses}>
                <WorkspaceInvitationAcceptButton
                  token={token}
                  onAccepted={(response) => {
                    setAccepted(response);
                    setError(null);
                  }}
                />
                <button
                  type="button"
                  className={dangerButtonClasses}
                  onClick={() => setShowDeclineConfirm(true)}
                  disabled={isDeclining}
                >
                  {isDeclining ? (
                    t("actions.saving")
                  ) : (
                    <>
                      <XCircle {...defaultIconProps} aria-hidden />
                      {t("workspaces.invitations.reject_button")}
                    </>
                  )}
                </button>
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic dark:text-slate-400">
                {t("workspaces.invitations.cannot_accept")}
              </p>
            )
          ) : (
            <div className={actionStackClasses}>
              <Link
                to={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                className={secondaryButtonClasses}
                onClick={rememberRedirect}
              >
                <SignIn {...defaultIconProps} aria-hidden />
                {t("auth.signin.to_continue")}
              </Link>
              <Link
                to={`/register?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                className={primaryButtonClasses}
                onClick={rememberRedirect}
              >
                <UserPlus {...defaultIconProps} aria-hidden />
                {t("auth.signup.create_account")}
              </Link>
            </div>
          )}
        </section>
      )}

      {declineSuccess && (
        <section className={cardClasses} role="status">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{t("workspaces.invitations.declined_title")}</h2>
          <p className="text-slate-600 dark:text-slate-400">{t("workspaces.invitations.declined_message")}</p>
        </section>
      )}

      {accepted && (
        <section className={successCardClasses} role="status">
          <h2 className="text-2xl font-bold mb-2">{t("workspaces.invitations.success_title")}</h2>
          <p className="mb-6" dangerouslySetInnerHTML={{ __html: t("workspaces.invitations.success_message", { name: `<strong>${accepted.workspace.name}</strong>` }) }} />
          <div className={actionStackClasses}>
            <button
              type="button"
              className={primaryButtonClasses}
              onClick={() => navigate(workspaceLink)}
            >
              <ArrowRight {...defaultIconProps} aria-hidden />
              {t("navigation.to_workspace")}
            </button>
            <Link to="/workspaces" className={secondaryButtonClasses}>
              <SquaresFourIcon {...defaultIconProps} aria-hidden />
              {t("navigation.to_workspaces")}
            </Link>
          </div>
        </section>
      )}

      <ConfirmDialog
        open={showDeclineConfirm}
        title={t("workspaces.invitations.reject_confirm")}
        confirmLabel={t("workspaces.invitations.reject_button")}
        confirmLoading={isDeclining}
        confirmTone="danger"
        cancelLabel={t("actions.cancel")}
        onCancel={() => setShowDeclineConfirm(false)}
        onConfirm={async () => {
          await handleDeclineInvitation();
          setShowDeclineConfirm(false);
        }}
      />
    </PageShell>
  );
}

function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} ${days === 1 ? "day" : "days"}`;
  }
  if (hours > 0) {
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  if (minutes > 0) {
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  }
  return `${seconds} ${seconds === 1 ? "second" : "seconds"}`;
}
